/**
 * Session du jour — algo Chiron (DEC-014 utility-first cyanotype).
 *
 * Genere une playlist de N jeux pour la session du jour avec :
 *   - pool eligible : tous les jeux livres (24)
 *   - score "due" SM-2 lite : jeux pas joues recemment ranked plus haut
 *   - equilibre rolling 7j : equilibre les categories cognitives joues
 *     dans les 7 derniers jours pour proposer celles sous-representees
 *   - interleaving : pas 2 jeux de la meme categorie d'affilee
 *   - calibration niveau : level - 1 pour les jeux jamais joues (cold start)
 *     ou jamais converged ces 7 derniers jours
 *   - duree variable : N adapte cible (default 12 jeux ~ 12 min)
 *   - cold start J1 : si aucun progres, on selectionne 12 jeux aleatoires
 *     equilibres sur les 6 categories
 *
 * Pure fonction — sans cote effects, deterministique pour un seed donne
 * (utilise pour les tests vitest).
 */
import { ACTIVE_CATEGORIES, ALL_GAME_SLUGS, GAME_TO_CATEGORY, } from './categories';
const DAY_MS = 24 * 3600 * 1000;
const ROLLING_DAYS = 7;
const DEFAULT_COUNT = 12;
/**
 * Hash deterministique str -> [0,1] pour fallback PRNG quand rng absent.
 * Pas un vrai random, mais utile pour stabilite des tests sans seed explicite.
 */
function hashStr(s) {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i += 1) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return ((h >>> 0) / 0xffffffff);
}
function daysAgo(iso, now) {
    if (!iso)
        return Number.POSITIVE_INFINITY;
    const ts = Date.parse(iso);
    if (!Number.isFinite(ts))
        return Number.POSITIVE_INFINITY;
    return Math.max(0, (now - ts) / DAY_MS);
}
/**
 * Score "due" SM-2 lite : plus un jeu n'a pas ete joue depuis longtemps
 * (par rapport a un intervalle cible base sur le niveau best), plus son
 * score monte. Cap a 1.
 */
function dueScore(daysSince, bestLevel) {
    if (!Number.isFinite(daysSince))
        return 1; // jamais joue -> max
    // Intervalle cible : 1 jour au level 1, 2j level 2, 3j level 3, ... cap 7j.
    const target = Math.min(7, Math.max(1, Math.round(bestLevel * 0.6)));
    return Math.min(1, daysSince / target);
}
/**
 * Boost categorie : +0.3 si la categorie a ete sous-representee (< 1 jeu)
 * dans les 7 derniers jours, 0 sinon. Equilibre rolling.
 */
function buildCatBalance(entries, now) {
    // Seuls les 6 domaines actifs M3 entrent dans l'équilibre rolling.
    // Perception + motor exclus (pending, 0 jeux).
    const counts = {};
    for (const cat of ACTIVE_CATEGORIES)
        counts[cat.slug] = 0;
    for (const e of entries) {
        if (!e.lastRunAt)
            continue;
        if (daysAgo(e.lastRunAt, now) > ROLLING_DAYS)
            continue;
        const cat = GAME_TO_CATEGORY[e.slug];
        if (cat && cat in counts)
            counts[cat] = (counts[cat] ?? 0) + 1;
    }
    const out = {};
    for (const cat of ACTIVE_CATEGORIES) {
        out[cat.slug] = (counts[cat.slug] ?? 0) < 1 ? 0.3 : 0;
    }
    return out;
}
/**
 * Interleaving : reordonne la liste pour qu'aucun jeu de la meme categorie
 * ne soit consecutif (best effort). Utilise un round-robin par categorie.
 */
function interleave(items) {
    const buckets = new Map();
    for (const it of items) {
        const arr = buckets.get(it.category) ?? [];
        arr.push(it);
        buckets.set(it.category, arr);
    }
    const sortedBuckets = Array.from(buckets.values()).sort((a, b) => b.length - a.length);
    const out = [];
    while (sortedBuckets.some((b) => b.length > 0)) {
        sortedBuckets.sort((a, b) => b.length - a.length);
        let placed = false;
        for (const bucket of sortedBuckets) {
            if (bucket.length === 0)
                continue;
            const last = out[out.length - 1];
            if (last && last.category === bucket[0].category)
                continue;
            out.push(bucket.shift());
            placed = true;
            break;
        }
        if (!placed) {
            // Fallback : impossible d'eviter 2 cat consecutives, on prend la
            // premiere bucket non vide.
            for (const bucket of sortedBuckets) {
                if (bucket.length > 0) {
                    out.push(bucket.shift());
                    break;
                }
            }
        }
    }
    return out;
}
/**
 * Niveau de calibration : si jamais joue ou pas converged depuis > 7j,
 * on retire 1 niveau (mais cap a 1) pour eviter la frustration.
 */
function calibratedLevel(bestLevel, daysSince) {
    if (!Number.isFinite(daysSince) || daysSince > ROLLING_DAYS) {
        return Math.max(1, bestLevel - 1);
    }
    return Math.max(1, bestLevel);
}
/**
 * Cold start J1 : aucune progression -> 12 jeux equilibres aleatoires
 * (2 par categorie sur 6 cat).
 */
function coldStartPlaylist(count, rng) {
    // Cold start sur les 6 domaines actifs uniquement (perception/motor
    // pending = pas de jeux à proposer). Pas de duplicate : si une cat a
    // moins de jeux que perCat, on cap à gameSlugs.length.
    const perCat = Math.ceil(count / ACTIVE_CATEGORIES.length);
    const items = [];
    for (const cat of ACTIVE_CATEGORIES) {
        if (cat.gameSlugs.length === 0)
            continue;
        const shuffled = [...cat.gameSlugs].sort(() => rng() - 0.5);
        const take = Math.min(perCat, shuffled.length);
        for (let i = 0; i < take && items.length < count; i += 1) {
            items.push({ slug: shuffled[i], category: cat.slug, level: 1 });
        }
    }
    // Si le quota perCat n'a pas suffit (cats maigres comme language=1),
    // on complète le reste avec les jeux non encore pris, en priorité sur
    // les cats à fort pool.
    if (items.length < count) {
        const used = new Set(items.map((it) => it.slug));
        const remaining = ACTIVE_CATEGORIES.flatMap((c) => c.gameSlugs.filter((g) => !used.has(g)).map((g) => ({ slug: g, cat: c.slug })));
        const shuffled = remaining.sort(() => rng() - 0.5);
        for (const r of shuffled) {
            if (items.length >= count)
                break;
            items.push({ slug: r.slug, category: r.cat, level: 1 });
        }
    }
    return interleave(items).slice(0, count);
}
/**
 * Genere la playlist session du jour.
 */
export function buildSessionDuJour(progressionEntries, options = {}) {
    const count = options.count ?? DEFAULT_COUNT;
    const now = options.now ?? Date.now();
    const rng = options.rng ?? Math.random;
    const byGame = new Map(progressionEntries.map((e) => [e.slug, e]));
    const playedCount = progressionEntries.filter((e) => e.lastRunAt).length;
    // Cold start J1 — aucune progression jouee
    if (playedCount === 0) {
        return coldStartPlaylist(count, rng);
    }
    // Score chaque jeu eligible
    const catBoosts = buildCatBalance(progressionEntries, now);
    const scored = [];
    for (const slug of ALL_GAME_SLUGS) {
        const entry = byGame.get(slug);
        const cat = GAME_TO_CATEGORY[slug];
        const bestLevel = entry?.bestLevel ?? 1;
        const dSince = daysAgo(entry?.lastRunAt ?? null, now);
        scored.push({
            slug,
            category: cat,
            bestLevel,
            daysSinceLast: dSince,
            dueScore: dueScore(dSince, bestLevel),
            catBalanceBoost: catBoosts[cat] ?? 0,
        });
    }
    // Tri par score combine descendant (due + boost cat + jitter petit)
    scored.sort((a, b) => {
        const sa = a.dueScore + a.catBalanceBoost + hashStr(a.slug) * 0.05;
        const sb = b.dueScore + b.catBalanceBoost + hashStr(b.slug) * 0.05;
        return sb - sa;
    });
    // Selection top N en garantissant diversite categories : on garde au moins
    // 1 jeu par categorie (si possible) avant de remplir le reste par score.
    const seen = new Set();
    const seenCats = new Set();
    const selected = [];
    // Pass 1 : 1 jeu par categorie active (top par score dans chaque cat)
    for (const cat of ACTIVE_CATEGORIES) {
        const top = scored.find((s) => s.category === cat.slug && !seen.has(s.slug));
        if (top && selected.length < count) {
            seen.add(top.slug);
            seenCats.add(top.category);
            selected.push({
                slug: top.slug,
                category: top.category,
                level: calibratedLevel(top.bestLevel, top.daysSinceLast),
            });
        }
    }
    // Pass 2 : remplit jusqu'a count, top score sans contrainte
    for (const s of scored) {
        if (selected.length >= count)
            break;
        if (seen.has(s.slug))
            continue;
        seen.add(s.slug);
        selected.push({
            slug: s.slug,
            category: s.category,
            level: calibratedLevel(s.bestLevel, s.daysSinceLast),
        });
    }
    // Pas eviter d'utiliser seenCats en stricte mais c'est utile pour future
    // metrique (ne pas warning unused).
    void seenCats;
    // Interleaving final
    return interleave(selected).slice(0, count);
}
//# sourceMappingURL=session-du-jour.js.map